import { ChevronDown, ChevronRight, Pencil, RotateCcw } from "lucide-react";
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, formatMs } from "@/lib/utils";
import { isZoomed, scaleBar, type ViewWindow, zoomWindowFromPixels } from "@/lib/waterfall-zoom";

/** Minimum drag distance (px) that counts as a zoom rather than a click. */
const DRAG_THRESHOLD_PX = 6;
/** Smallest zoomable span (ms) — a natural floor that stops degenerate zooms. */
const MIN_SPAN_MS = 1;

/** A single phase (or sub-phase) in the timing waterfall. */
export interface Phase {
  label: string;
  /** Start offset in ms — absolute for top-level phases, parent-relative for children. */
  startMs: number;
  durationMs: number;
  color: string;
  tooltip?: string;
  children?: Phase[];
  /** Render children expanded on first paint (chevron still collapses them). */
  defaultExpanded?: boolean;
}

/**
 * Flatten the phases that are visible on first paint (top-level rows plus the
 * children of default-expanded rows), converting child offsets to absolute
 * timeline positions. Used for the zoom editor's jump-to-phase buttons.
 */
export function flattenVisiblePhases(phases: Phase[], offsetMs = 0): Phase[] {
  const flattened: Phase[] = [];
  for (const phase of phases) {
    const startMs = offsetMs + phase.startMs;
    flattened.push({ ...phase, startMs });
    if (phase.children && phase.defaultExpanded) {
      flattened.push(...flattenVisiblePhases(phase.children, startMs));
    }
  }
  return flattened;
}

/** Convert seconds to rounded milliseconds, treating null/undefined as zero. */
export function msFromSeconds(seconds: number | undefined | null): number {
  if (seconds == null) return 0;
  return Math.round(seconds * 1000);
}

export function formatPercent(part: number, total: number): string {
  if (total === 0) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

/** A single Gantt bar row with optional expandable sub-phases. */
export function GanttBar({
  phase,
  totalMs,
  viewWindow,
  depth,
  failedLabel,
}: {
  phase: Phase;
  totalMs: number;
  viewWindow: ViewWindow;
  depth: number;
  failedLabel?: string | null;
}) {
  const [expanded, setExpanded] = useState(phase.defaultExpanded ?? false);
  const hasChildren = phase.children && phase.children.length > 0;
  const isFailed = failedLabel === phase.label;
  const indent = depth > 0;
  const bar = scaleBar(phase.startMs, phase.durationMs, viewWindow);

  return (
    <div>
      <button
        type="button"
        aria-expanded={hasChildren ? expanded : undefined}
        className={cn(
          "flex w-full items-center gap-2 py-1 text-sm",
          hasChildren && "cursor-pointer hover:bg-accent/50 rounded",
          !hasChildren && "cursor-default",
          isFailed && "bg-rose-500/5 border-l-2 border-l-rose-500 rounded-l-none",
        )}
        onClick={() => hasChildren && setExpanded(!expanded)}
        disabled={!hasChildren}
      >
        {/* Phase label. Indentation lives INSIDE this fixed-width column so
            the Duration and Timeline columns stay pixel-aligned across
            depths — any outer margin/padding on nested rows would shift
            their bar track relative to the parent's, making child bars
            appear offset from the spans they belong to. */}
        <div
          className="flex w-36 shrink-0 items-center gap-1"
          style={indent ? { paddingLeft: depth * 12 } : undefined}
        >
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="size-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-3 text-muted-foreground" />
            )
          ) : (
            <span className="w-3" />
          )}
          {phase.tooltip ? (
            <Tooltip>
              <TooltipTrigger
                render={<span />}
                className={cn(
                  "truncate underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 cursor-help",
                  isFailed
                    ? "font-medium text-rose-500"
                    : indent
                      ? "text-muted-foreground"
                      : "font-medium text-foreground",
                )}
              >
                {phase.label}
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                {phase.tooltip}
              </TooltipContent>
            </Tooltip>
          ) : (
            <span
              className={cn(
                "truncate",
                isFailed
                  ? "font-medium text-rose-500"
                  : indent
                    ? "text-muted-foreground"
                    : "font-medium text-foreground",
              )}
            >
              {phase.label}
            </span>
          )}
        </div>

        {/* Duration + percent */}
        <div className="w-24 shrink-0 text-right font-mono text-xs text-muted-foreground">
          {formatMs(phase.durationMs)}{" "}
          <span className="text-muted-foreground/60">
            ({formatPercent(phase.durationMs, totalMs)})
          </span>
        </div>

        {/* Gantt bar */}
        <div className="relative flex-1 h-5">
          <div className="absolute inset-0 rounded-sm bg-muted/30" />
          {bar.visible ? (
            <div
              className={cn("absolute top-0 h-5 rounded-sm transition-all", phase.color)}
              style={{
                left: `${bar.leftPercent}%`,
                width: `${bar.widthPercent}%`,
                minWidth: phase.durationMs > 0 ? "2px" : "0",
              }}
            />
          ) : (
            // Bar is off the zoomed window — pin a faint sliver to the near edge.
            bar.offscreen && (
              <div
                className={cn(
                  "absolute top-0 h-5 w-1 opacity-40",
                  bar.offscreen === "before" ? "left-0 rounded-l-sm" : "right-0 rounded-r-sm",
                  phase.color,
                )}
                title={
                  bar.offscreen === "before" ? "Before the zoomed range" : "After the zoomed range"
                }
              />
            )
          )}
        </div>
      </button>

      {/* Expanded sub-phases. No wrapper margin/border — indentation is
          handled inside each row's label column to keep bar tracks aligned. */}
      {expanded && hasChildren && (
        <div>
          {(phase.children ?? []).map((child) => (
            <GanttBar
              key={child.label}
              phase={{
                ...child,
                startMs: phase.startMs + child.startMs,
              }}
              totalMs={totalMs}
              viewWindow={viewWindow}
              depth={depth + 1}
              failedLabel={failedLabel}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Round ms to a tidy seconds string for display (e.g. 420400 -> "420.4"). */
function msToSecondsLabel(ms: number): string {
  return String(Math.round(ms / 100) / 10);
}

/** Exact seconds string for seeding an input (never rounds past the real bound). */
function msToSecondsInput(ms: number): string {
  return String(Math.round(ms) / 1000);
}

/**
 * Click-to-edit popover for the zoom window: type an exact from/to range in
 * seconds, reset to the full range, or jump to any phase's span.
 */
function RangeEditor({
  viewWindow,
  totalMs,
  phases,
  windowLabel,
  zoomed,
  onApply,
}: {
  viewWindow: ViewWindow;
  totalMs: number;
  phases: Phase[];
  windowLabel: string;
  zoomed: boolean;
  onApply: (next: ViewWindow) => void;
}) {
  const [open, setOpen] = useState(false);
  const [fromDraft, setFromDraft] = useState("");
  const [toDraft, setToDraft] = useState("");
  const fromId = useId();
  const toId = useId();
  const hintId = useId();

  // Re-seed the drafts from the live window each time the editor opens.
  useEffect(() => {
    if (open) {
      setFromDraft(msToSecondsInput(viewWindow.startMs));
      setToDraft(msToSecondsInput(viewWindow.endMs));
    }
  }, [open, viewWindow.startMs, viewWindow.endMs]);

  const totalSeconds = totalMs / 1000;
  const fromSeconds = Number(fromDraft);
  const toSeconds = Number(toDraft);
  const fromValid = fromDraft.trim() !== "" && Number.isFinite(fromSeconds) && fromSeconds >= 0;
  const toValid =
    toDraft.trim() !== "" && Number.isFinite(toSeconds) && toSeconds <= totalSeconds + 1e-6;
  const valid = fromValid && toValid && toSeconds - fromSeconds >= MIN_SPAN_MS / 1000;

  const applyWindow = (next: ViewWindow) => {
    onApply(next);
    setOpen(false);
  };

  const applyDraft = () => {
    if (!valid) return;
    applyWindow({
      startMs: Math.max(0, fromSeconds * 1000),
      endMs: Math.min(totalMs, toSeconds * 1000),
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        title="Set zoom range"
        className={cn(
          "flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none data-[popup-open]:bg-muted/60 data-[popup-open]:text-foreground",
          zoomed ? "font-mono text-[11px] tabular-nums" : "text-muted-foreground/70",
        )}
      >
        {zoomed ? windowLabel : "drag to zoom"}
        <Pencil className="size-2.5 opacity-50" />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 gap-2.5 p-3">
        <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Zoom range (seconds)
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor={fromId} className="text-[11px] text-muted-foreground">
              From
            </Label>
            <Input
              id={fromId}
              type="number"
              inputMode="decimal"
              min={0}
              max={totalSeconds}
              step={0.1}
              autoFocus
              value={fromDraft}
              onChange={(event) => setFromDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") applyDraft();
              }}
              aria-invalid={!fromValid}
              aria-describedby={valid ? undefined : hintId}
              className="h-8 font-mono text-xs"
            />
          </div>
          <div className="flex-1 space-y-1">
            <Label htmlFor={toId} className="text-[11px] text-muted-foreground">
              To
            </Label>
            <Input
              id={toId}
              type="number"
              inputMode="decimal"
              min={0}
              max={totalSeconds}
              step={0.1}
              value={toDraft}
              onChange={(event) => setToDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") applyDraft();
              }}
              aria-invalid={!toValid}
              aria-describedby={valid ? undefined : hintId}
              className="h-8 font-mono text-xs"
            />
          </div>
        </div>
        {!valid && (
          <p id={hintId} className="text-[11px] text-destructive">
            Enter a range within 0–{msToSecondsLabel(totalMs)}s, with From before To.
          </p>
        )}
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground">
            full: 0–{msToSecondsLabel(totalMs)}s
          </span>
          <div className="flex gap-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7"
              onClick={() => applyWindow({ startMs: 0, endMs: totalMs })}
            >
              Full range
            </Button>
            <Button type="button" size="sm" className="h-7" onClick={applyDraft} disabled={!valid}>
              Apply
            </Button>
          </div>
        </div>
        {phases.length > 1 && (
          <div className="space-y-1.5 border-t pt-2.5">
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Jump to phase
            </div>
            <div className="flex flex-wrap gap-1.5">
              {phases.map((phase) => (
                <Button
                  key={phase.label}
                  type="button"
                  variant="outline"
                  size="xs"
                  className="font-normal"
                  onClick={() =>
                    applyWindow({
                      startMs: phase.startMs,
                      endMs: Math.min(
                        totalMs,
                        phase.startMs + Math.max(phase.durationMs, MIN_SPAN_MS),
                      ),
                    })
                  }
                >
                  {phase.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Waterfall shell: header row, phase rows with GanttBar, and total footer.
 * Both TimingWaterfall and CryodTimingWaterfall delegate to this after
 * building their own phase lists.
 */
export function WaterfallShell({
  phases,
  totalMs,
  failedLabel,
  totalRestoreMs,
}: {
  phases: Phase[];
  totalMs: number;
  failedLabel: string | null;
  totalRestoreMs?: number | null;
}) {
  // The visible sub-range of [0, totalMs]; drag the timeline to narrow it.
  const [viewWindow, setViewWindow] = useState<ViewWindow>({ startMs: 0, endMs: totalMs });
  const [selection, setSelection] = useState<{ left: number; width: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragStartXRef = useRef<number | null>(null);
  const activePointerIdRef = useRef<number | null>(null);

  // Reset the zoom whenever the underlying data (and thus the domain) changes.
  useEffect(() => {
    setViewWindow({ startMs: 0, endMs: totalMs });
  }, [totalMs]);

  const endDrag = useCallback(() => {
    dragStartXRef.current = null;
    activePointerIdRef.current = null;
    setSelection(null);
    setDragging(false);
  }, []);

  // Escape cancels an in-progress selection without zooming.
  useEffect(() => {
    if (!dragging) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") endDrag();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dragging, endDrag]);

  const localX = (track: HTMLDivElement, clientX: number) => {
    const rect = track.getBoundingClientRect();
    return Math.max(0, Math.min(clientX - rect.left, rect.width));
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (event.button !== 0 || dragStartXRef.current !== null || !track) return;
    activePointerIdRef.current = event.pointerId;
    dragStartXRef.current = localX(track, event.clientX);
    setDragging(true);
    track.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = dragStartXRef.current;
    const track = trackRef.current;
    if (start == null || !track || event.pointerId !== activePointerIdRef.current) return;
    const current = localX(track, event.clientX);
    const width = Math.abs(current - start);
    setSelection(width >= DRAG_THRESHOLD_PX ? { left: Math.min(start, current), width } : null);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = dragStartXRef.current;
    const track = trackRef.current;
    if (event.pointerId !== activePointerIdRef.current) return;
    endDrag();
    if (start == null || !track) return;
    const end = localX(track, event.clientX);
    if (Math.abs(end - start) < DRAG_THRESHOLD_PX) return;
    const next = zoomWindowFromPixels(
      start,
      end,
      track.getBoundingClientRect().width,
      viewWindow,
      MIN_SPAN_MS,
    );
    if (next) setViewWindow(next);
  };

  const resetZoom = () => setViewWindow({ startMs: 0, endMs: totalMs });
  const zoomed = isZoomed(viewWindow, totalMs);
  const windowLabel = `${formatMs(Math.round(viewWindow.startMs))}–${formatMs(Math.round(viewWindow.endMs))}`;

  if (phases.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">No timing data available</div>
    );
  }

  return (
    <TooltipProvider>
      <div>
        {/* Header */}
        <div className="flex items-center gap-2 pb-2 mb-2 border-b text-xs text-muted-foreground">
          <div className="w-36 shrink-0 pl-4">Phase</div>
          <div className="w-24 shrink-0 text-right">Duration</div>
          <div className="flex flex-1 items-center justify-between gap-2">
            <span>Timeline</span>
            <span className="flex items-center gap-2">
              <RangeEditor
                viewWindow={viewWindow}
                totalMs={totalMs}
                phases={flattenVisiblePhases(phases)}
                windowLabel={windowLabel}
                zoomed={zoomed}
                onApply={setViewWindow}
              />
              {zoomed && (
                <button
                  type="button"
                  onClick={resetZoom}
                  className="flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors"
                  aria-label="Reset timeline zoom to full range"
                >
                  <RotateCcw className="size-3" />
                  reset
                </button>
              )}
            </span>
          </div>
        </div>

        {/* Phase rows, with a transparent drag layer over the Timeline column */}
        <div className="relative">
          <div className="space-y-0.5">
            {phases.map((phase) => (
              <GanttBar
                key={phase.label}
                phase={phase}
                totalMs={totalMs}
                viewWindow={viewWindow}
                depth={0}
                failedLabel={failedLabel}
              />
            ))}
          </div>

          {/* Spacers mirror the row columns so the track lines up with the bars. */}
          <div className="pointer-events-none absolute inset-0 flex gap-2" aria-hidden="true">
            <div className="w-36 shrink-0" />
            <div className="w-24 shrink-0" />
            {/* biome-ignore lint/a11y/noStaticElementInteractions: pointer-only zoom; keyboard/AT users use the Reset button and aria-live region below */}
            <div
              ref={trackRef}
              className="pointer-events-auto relative flex-1 cursor-col-resize select-none touch-pan-y"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={endDrag}
              onDoubleClick={resetZoom}
            >
              {selection && (
                <div
                  className="absolute inset-y-0 rounded-sm border-x border-primary/60 bg-primary/20"
                  style={{ left: `${selection.left}px`, width: `${selection.width}px` }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="mt-3 pt-2 border-t flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">Total:</span>
          <span className="font-mono font-medium">{formatMs(totalMs)}</span>
          {totalRestoreMs != null && totalRestoreMs !== totalMs && (
            <>
              <span className="text-muted-foreground">Restore:</span>
              <span className="font-mono font-medium">{formatMs(totalRestoreMs)}</span>
            </>
          )}
        </div>

        <span className="sr-only" aria-live="polite">
          {zoomed ? `Timeline zoomed to ${windowLabel}` : "Timeline showing full range"}
        </span>
      </div>
    </TooltipProvider>
  );
}
